import { Send, X } from 'lucide-react';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import {
  Badge,
  Button,
  Dialog,
  ErrorState,
  LoadingState,
  useToast,
} from '@/components/ui';
import { useSession } from '@/features/auth/session-context';
import {
  useClinicChat,
  useClinicChatActions,
} from '@/features/clinic-chat/api';
import { isApiError } from '@/lib/api';
import { chatTime } from './chat-format';
import styles from './PatientChatPage.module.css';

/** The API's limit for one message (RL-994 MESSAGE_MAX). */
export const CHAT_MESSAGE_MAX = 1000;

/**
 * RL-1674 (gap #4): one chat: the messages, a reply box, `Close
 * conversation` and a link to the patient's record. Opening it marks it read
 * for the branch's nurses; new messages arrive live (RL-1150 stream on the
 * page). The patient gets a push "New message from the clinic" for each
 * reply (never the text).
 */
export function ChatConversation({ id }: { id: string }) {
  const { t } = useTranslation();
  const { hasPermission } = useSession();
  const toast = useToast();
  const { data: chat, isLoading, error, refetch } = useClinicChat(id);
  const a = useClinicChatActions();
  const [text, setText] = useState('');
  // Review: closing is final for this chat, so it is confirmed first.
  const [confirmClose, setConfirmClose] = useState(false);
  const readFor = useRef<string | null>(null);
  const end = useRef<HTMLLIElement>(null);
  const canReply = hasPermission('clinic_chat:reply');

  // Opening the chat marks it read (once per chat and new message).
  const unread = chat?.unreadCount ?? 0;
  const key = chat ? `${chat.id}:${chat.lastMessageAt}` : null;
  useEffect(() => {
    if (!chat || !key || readFor.current === key) return;
    if (unread === 0 && chat.staffReadAt) return;
    readFor.current = key;
    a.read.mutate({
      id: chat.id,
      upTo: chat.messages[chat.messages.length - 1]?.sentAt,
    });
  }, [chat, key, unread, a.read]);

  useEffect(() => {
    end.current?.scrollIntoView?.({ block: 'end' });
  }, [chat?.messages.length]);

  const fail = (err: unknown) => {
    if (isApiError(err) && err.code === 'CHAT_CHANGED') void refetch();
    toast.show({
      tone: 'error',
      title:
        isApiError(err) && err.code === 'CHAT_CHANGED'
          ? t('patientChat.changed')
          : t('patientChat.notSent'),
      body: isApiError(err) ? err.message : undefined,
      error: err,
    });
  };

  if (isLoading) return <LoadingState />;
  if (error) return <ErrorState error={error} onRetry={() => void refetch()} />;
  if (!chat) return null;
  const closed = chat.status === 'closed';

  function send(e: FormEvent, close = false) {
    e.preventDefault();
    const body = text.trim();
    if (!chat || !body) return;
    a.reply
      .mutateAsync({ id: chat.id, text: body, version: chat.version, close })
      .then(() => {
        setText('');
        toast.show({
          tone: 'success',
          title: close ? t('patientChat.sentClosed') : t('patientChat.sent'),
        });
      })
      .catch(fail);
  }

  return (
    <section
      aria-label={t('patientChat.conversationWith', {
        name: chat.patient.fullName,
      })}
    >
      <div className={styles.head}>
        <span className={styles.headName}>{chat.patient.fullName}</span>
        {chat.status === 'open' && (
          <Badge tone="warning">{t('patientChat.waiting')}</Badge>
        )}
        {chat.status === 'answered' && (
          <Badge tone="success">{t('patientChat.answered')}</Badge>
        )}
        {closed && <Badge>{t('patientChat.closedBadge')}</Badge>}
        {hasPermission('patients:read') && (
          <Link to={`/patients/${chat.patient.id}`}>
            {t('patientChat.record')}
          </Link>
        )}
      </div>
      {chat.identityPending && (
        <p className={styles.warn} role="note">
          {t('patientChat.identityPendingNote')}
        </p>
      )}
      {chat.receivedOutOfHours && chat.status === 'open' && (
        <p className={styles.warn} role="note">
          {t('patientChat.outOfHoursNote')}
        </p>
      )}
      <ol
        className={styles.messages}
        aria-label={t('patientChat.messagesLabel')}
      >
        {chat.messages.map((m) => (
          <li
            key={m.id}
            className={[
              styles.msg,
              m.authorType === 'staff' && styles.fromClinic,
            ]
              .filter(Boolean)
              .join(' ')}
          >
            {m.text}
            <span className={styles.meta}>
              {m.authorType === 'staff'
                ? (m.staff?.fullName ?? t('patientChat.clinic'))
                : chat.patient.fullName}{' '}
              · {chatTime(m.sentAt)}
            </span>
          </li>
        ))}
        <li ref={end} aria-hidden="true" />
      </ol>
      {closed ? (
        <p className={styles.empty}>
          {chat.closeReason === 'account_deleted'
            ? t('patientChat.closedDeleted')
            : t('patientChat.closedNote')}
        </p>
      ) : canReply ? (
        <form onSubmit={(e) => send(e)}>
          <label className="eyebrow" htmlFor="chat-reply">
            {t('patientChat.reply')}
          </label>
          <textarea
            id="chat-reply"
            className={styles.textarea}
            value={text}
            maxLength={CHAT_MESSAGE_MAX}
            onChange={(e) => setText(e.target.value)}
            placeholder={t('patientChat.replyHint')}
          />
          <div className={styles.actions}>
            <Button
              variant="ghost"
              icon={<X size={16} />}
              onClick={() => setConfirmClose(true)}
            >
              {t('patientChat.close')}
            </Button>
            <Button
              disabled={!text.trim()}
              loading={a.reply.isPending}
              onClick={(e) => send(e, true)}
            >
              {t('patientChat.sendClose')}
            </Button>
            <Button
              type="submit"
              variant="primary"
              icon={<Send size={16} />}
              disabled={!text.trim()}
              loading={a.reply.isPending}
            >
              {t('patientChat.send')}
            </Button>
          </div>
        </form>
      ) : null}
      <Dialog
        open={confirmClose}
        onClose={() => setConfirmClose(false)}
        variant="modal"
        title={t('patientChat.closeTitle')}
        footer={
          <>
            <Button onClick={() => setConfirmClose(false)}>
              {t('patientChat.keepOpen')}
            </Button>
            <Button
              variant="danger"
              loading={a.close.isPending}
              onClick={() =>
                void a.close
                  .mutateAsync({ id: chat.id, version: chat.version })
                  .then(() => {
                    setConfirmClose(false);
                    toast.show({
                      tone: 'success',
                      title: t('patientChat.closedToast'),
                    });
                  })
                  .catch((err: unknown) => {
                    setConfirmClose(false);
                    fail(err);
                  })
              }
            >
              {t('patientChat.close')}
            </Button>
          </>
        }
      >
        <p>{t('patientChat.closeBody', { name: chat.patient.fullName })}</p>
      </Dialog>
    </section>
  );
}
